"use client";

import { ScrollReveal } from "@/components/marketing/animations";
import { ClipVideo } from "@/components/marketing/clip-video";

/**
 * Real-product screen recordings for /apps/finance — captured from the live
 * terminal (no mockups), encoded as muted autoplay-loop MP4 (~15x lighter than
 * GIF, smoother). An IntersectionObserver plays each clip when it scrolls into
 * view and pauses it when it leaves — reliable autoplay everywhere + light.
 */
const FRAME =
  "relative rounded-[16px] border hairline-strong bg-card/80 overflow-hidden " +
  "shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]";

export function GifFrame({
  src,
  label,
  caption,
}: {
  src: string;
  label: string;
  caption?: string;
}) {
  return (
    <figure className={FRAME}>
      <div className="flex items-center gap-1.5 px-3 h-7 border-b hairline bg-foreground/[0.02]">
        <span className="h-2 w-2 rounded-full bg-foreground/15" aria-hidden />
        <span className="h-2 w-2 rounded-full bg-foreground/15" aria-hidden />
        <span className="h-2 w-2 rounded-full bg-foreground/15" aria-hidden />
        <span className="ml-2 font-mono text-[10px] tracking-[0.14em] uppercase text-foreground/45">
          {label}
        </span>
      </div>
      {/* ClipVideo matches its box to the clip's real aspect → fills, never crops. */}
      <ClipVideo src={src} />
      {caption && (
        <figcaption className="px-4 py-2.5 text-[12.5px] leading-snug text-foreground/60 border-t hairline">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

const GALLERY = [
  {
    src: "/finance/gifs/finance-snapshot.mp4",
    label: "Stock detail",
    caption: "Live quote, order book and time & sales, with an AI read of every name at a glance.",
  },
  {
    src: "/finance/gifs/finance-options.mp4",
    label: "Options & gamma",
    caption: "The whole chain, dealer gamma exposure, max pain and the implied-volatility surface.",
  },
  {
    src: "/finance/gifs/finance-transcripts.mp4",
    label: "Earnings-call transcripts",
    caption: "Read the actual call, speaker by speaker — then ask the terminal to summarise it.",
  },
  {
    src: "/finance/gifs/finance-heatmap.mp4",
    label: "Live market heatmap",
    caption: "The whole market by sector and size, green-to-red on the day.",
  },
];

export function FinanceGifGallery() {
  return (
    <div className="mt-10 grid gap-6 sm:grid-cols-2">
      {GALLERY.map((g, i) => (
        <ScrollReveal key={g.src} delay={60 * (i % 2)}>
          <GifFrame {...g} />
        </ScrollReveal>
      ))}
    </div>
  );
}
